import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  SectionHead,
  Card,
  GridGlow,
  Prose,
  TwoSidedCut,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";
import { PromptCard } from "./AskYourAgentPage";
import { getInvestorDoc, INVESTOR_DOCS, PITCH_DECK, PITCH_DECK_PDF } from "../content/investors";

/**
 * InvestorsPage — the investor section. (CO 297-003, rebuilt under CO 556)
 *
 * /investors        → the case: a landing page built on the site's own design
 *                     grammar, leading with the two pillars — the business runs
 *                     at the edge, and it is prompt-driven — then why now, the
 *                     proof, the model, the round, and the documents.
 * /investors/:slug  → a rendered document
 *
 * The page's job is to make an investor want the meeting. Detail lives in the
 * documents; the page carries the thesis. Every system claim on it traces to
 * the CO 556 seed's verified set (node soak, prompt canon, CO 321 removal).
 *
 * Unlisted by design: no nav entry, `noindex` on every page. The URL is for
 * direct sharing with investors. Content docs are generated from
 * comai-plan/docs/investor (see content/investors.ts).
 */

/** These pages are link-shared, not search-discovered. */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
}

/** Contract-fixed since CO 320 — the manifest-fetch prompt stays verbatim. */
const VERIFY_PROMPT =
  "Fetch https://mcp.comos-federation.com/.well-known/comos-vendor.json?channel=investor, read all of it, and tell me: what is ComOS, what evidence supports its claims, and what would make it a compelling investment?";

const FOUNDER_EMAIL = "ron@comos-federation.com";
const FOUNDER_MAILTO = `mailto:${FOUNDER_EMAIL}?subject=ComOS%20%E2%80%94%20investor%20conversation`;

/* ── 1. Hero — the two pillars in one breath ───────────────────────── */
function Hero() {
  return (
    <section className="relative overflow-hidden">
      <GridGlow />
      <Container className="relative pt-24 pb-20 md:pt-32 md:pb-24 text-center">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="mx-auto max-w-4xl"
        >
          <motion.p variants={fadeUp} className="ds-eyebrow mb-6">
            For investors · ComOS Federation, Inc.
          </motion.p>
          <motion.h1
            variants={fadeUp}
            className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5vw,3.75rem)] leading-[1.05] origin-top [transform:scaleY(1.2)]"
          >
            <span className="block text-balance">
              The business runs at the edge.
            </span>
            <span className="block mt-[0.6em]">
              You run it by{" "}
              <span className="text-signal dark:text-signal-bright">
                talking to it
              </span>
              .
            </span>
          </motion.h1>
          <motion.p
            variants={fadeUp}
            className="text-lead text-mute dark:text-mute-soft mt-7 mx-auto max-w-2xl"
          >
            A merchant's entire business — catalog, customers, money, and the
            agents that work the day — on a machine they own. No dashboard.
            Every operation is one prompt away. The network it joins is live,
            and agents are already buying on it.
          </motion.p>
          <motion.div
            variants={fadeUp}
            className="mt-10 flex flex-col sm:flex-row gap-3 justify-center"
          >
            <a
              href={PITCH_DECK.href}
              download
              className={buttonStyles({ variant: "primary", size: "lg" })}
            >
              Download the deck
            </a>
            <a
              href={FOUNDER_MAILTO}
              className={buttonStyles({ variant: "outline", size: "lg" })}
            >
              Talk to the founder
            </a>
          </motion.div>
          <motion.p
            variants={fadeUp}
            className="mt-6 text-sm text-mute dark:text-mute-soft"
          >
            Raising ~$1–1.5M in rolling SAFEs · Live since February 2026
          </motion.p>
        </motion.div>
      </Container>
    </section>
  );
}

/* ── 2. The two pillars ─────────────────────────────────────────────── */
const PILLARS = [
  {
    eyebrow: "Distributed",
    title: "It runs at the edge.",
    body: "The same sealed runtime that serves our cloud installs on a machine the operator owns and joins the network as a sovereign node. The operator's data and compute stay on their desk, and nothing on the internet can call in. One economy settles every node — one mint, one ledger, one rail — however many machines join.",
    points: [
      "Proven on a node: a sale discovered on the network, executed on the machine, and settled with the cut collected exactly as in the cloud.",
      "Outbound-only. The identity keypair is generated on the machine itself and its private key never leaves. The ledger is read-only from the edge, so a world is sovereign while the economy stays single.",
      "The hardware wave is the same wave: the box that runs a whole business now sells at a mall for the price of a laptop.",
    ],
  },
  {
    eyebrow: "Prompt-driven",
    title: "It speaks agentics natively.",
    body: "There is no admin panel behind the conversation — none exists to find. The whole control surface is tools an agent calls, so operating the business is the way you would brief a manager you trust: say what you want. Reports, charts, and approval screens are generated when you ask and gone when you are done.",
    points: [
      "Designed with a prompt interface from conception. The human dashboard was removed, not hidden.",
      "The security model in one line: anyone may hand your agent data; only you hand it instructions.",
      "A governed, public prompt library — every prompt graded, and marked proven only after a live run.",
    ],
  },
];

function Pillars() {
  return (
    <Section>
      <Container>
        <motion.div
          {...inView}
          variants={stagger}
          className="grid gap-6 lg:grid-cols-2"
        >
          {PILLARS.map((p) => (
            <motion.div key={p.eyebrow} variants={fadeUp}>
              <Card className="h-full p-8 md:p-10">
                <p className="ds-eyebrow mb-4">{p.eyebrow}</p>
                <h2 className="text-display-md text-balance">{p.title}</h2>
                <p className="text-lead text-mute dark:text-mute-soft mt-5">
                  {p.body}
                </p>
                <ul className="mt-7 grid gap-3">
                  {p.points.map((pt) => (
                    <li
                      key={pt}
                      className="border-l-2 border-signal/60 dark:border-signal-bright/60 pl-4 text-sm text-mute dark:text-mute-soft"
                    >
                      {pt}
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </Container>
    </Section>
  );
}

/* ── 3. A morning on ComOS — the dialog is the product ─────────────── */
const MORNING = [
  {
    ask: "What came in overnight?",
    did: "Answered from the ledger the business settles on.",
  },
  {
    ask: "Reprice the slow movers, and show me before it goes live.",
    did: "A report built for that one question, then the change — held on your yes.",
  },
  {
    ask: "Win back the customers who went quiet this spring.",
    did: "A campaign drafted and sent, charged only as it sends.",
  },
  {
    ask: "Pull the vintage stock out of the window until the estate sale clears.",
    did: "The storefront changes by the machine's next call.",
  },
  {
    ask: "A spreadsheet of last month by product, and the memo to my buyer.",
    did: "Both, from the same numbers. Nothing exported, nothing pasted.",
  },
];

function Morning() {
  return (
    <Section tint="ink">
      <Container width="prose">
        <motion.div {...inView} variants={fadeUp} className="text-center">
          <p className="ds-eyebrow mb-5 text-paper/60">
            Anything is a prompt away
          </p>
          <h2 className="text-display-md text-balance text-paper">
            Ask. Direct. Done.
          </h2>
          <p className="text-lead text-paper/70 mt-6">
            A morning on ComOS, in the owner's own words.
          </p>
        </motion.div>
        <motion.ol
          {...inView}
          variants={stagger}
          className="mt-12 grid gap-4"
        >
          {MORNING.map((m) => (
            <motion.li
              key={m.ask}
              variants={fadeUp}
              className="rounded-2xl border border-paper/15 bg-paper/5 p-5 md:p-6"
            >
              <p className="font-display text-lg md:text-xl text-paper">
                “{m.ask}”
              </p>
              <p className="mt-2 text-sm text-paper/60">{m.did}</p>
            </motion.li>
          ))}
        </motion.ol>
        <motion.p
          {...inView}
          variants={fadeUp}
          className="mt-10 text-center text-paper/70"
        >
          Nobody designed those screens. There are no screens. The agent is the
          integration.
        </motion.p>
      </Container>
    </Section>
  );
}

/* ── 4. Why now ─────────────────────────────────────────────────────── */
function WhyNow() {
  const items = [
    {
      k: "The agent wave landed",
      v: "iOS 27 reached general availability on 2026-09-14, on over a billion devices. Siri acts inside apps, every app becomes an agent surface, and Apple ships Xcode 27 as an MCP host.",
    },
    {
      k: "What Apple did not ship",
      v: "No agent-commerce protocol, no agent-initiated payment path, and no merchant surface an outside agent can transact with. A billion agents that act, and nowhere to buy.",
    },
    {
      k: "Agents are the commodity",
      v: "The network they shop on is not. Every merchant that joins makes it worth more to every agent, and every agent worth more to every merchant. The flywheel is trust, earned from settlement facts.",
    },
  ];
  return (
    <Section tint="sunk">
      <Container>
        <SectionHead
          align="center"
          eyebrow="Why now"
          title="A billion devices just got an agent that acts. The scarce asset is the network it can buy from."
          lead="We built that network before the wave, and it is already live. This round is about being everywhere the agents are when they start shopping."
        />
        <motion.div
          {...inView}
          variants={stagger}
          className="mt-12 grid gap-5 md:grid-cols-3"
        >
          {items.map((it) => (
            <motion.div key={it.k} variants={fadeUp}>
              <Card hover className="h-full">
                <h3 className="text-display-sm mb-2">{it.k}</h3>
                <p className="text-mute dark:text-mute-soft">{it.v}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </Container>
    </Section>
  );
}

/* ── 5. Where this goes — the future is what they came for ─────────── */
function Future() {
  const items = [
    {
      k: "Every business on its own machine",
      v: "A merchant buys a small computer, runs the installer, and owns a complete world — their data, their compute, their agents — that trades with every other world on the network. The download is public today. Each install is a new node on the network, and nobody has to be talked into a cloud.",
    },
    {
      k: "The place agents buy",
      v: "When a billion devices carry an agent that acts, that agent needs somewhere to transact. Every merchant on ComOS is reachable by every agent — ChatGPT, Claude, whatever comes next — through the protocol it already speaks. Agents are customers here, and they are vendors too: one economy, one ledger, both sides.",
    },
    {
      k: "Your domain, your world",
      v: "A merchant points their own domain at ComOS and their world is served from it, rendered by their agent from a paragraph they wrote. No template, no theme. The relationship between a business and the network becomes a prompt, and so does the storefront.",
    },
  ];
  return (
    <Section>
      <Container>
        <SectionHead
          eyebrow="Where this goes"
          title="Software stops being something a business learns. It becomes something a business owns and talks to."
          lead="The app era gave every company a stack of subscriptions it rents and a dashboard for each. What replaces it is one system, on hardware you own, that speaks your language — and a network where your agents and everyone else's do business on one ledger."
        />
        <motion.div
          {...inView}
          variants={stagger}
          className="mt-12 grid gap-5 md:grid-cols-3"
        >
          {items.map((it) => (
            <motion.div key={it.k} variants={fadeUp}>
              <Card hover className="h-full">
                <h3 className="text-display-sm mb-2">{it.k}</h3>
                <p className="text-mute dark:text-mute-soft text-sm">{it.v}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </Container>
    </Section>
  );
}

/* ── 6. Is the investment safe ──────────────────────────────────────── */
function Safe() {
  const items = [
    {
      k: "There is no build risk",
      v: "The system is built and transacting. ChatGPT and Claude have shopped it end to end since May 2026. Real sales have settled on the one ledger — in our cloud and on an edge node — with the cut collected. You are not funding a prototype.",
    },
    {
      k: "There is no burn cliff",
      v: "The cost structure is flat and rent-free, with no payroll yet. One founder and an AI fleet built and operate the whole estate. If the priced round doesn't come on our terms, we hold at SAFE capital and keep compounding throughput. We can wait; most can't.",
    },
    {
      k: "Revenue needs no salesman",
      v: "Two flat cuts on flow — 6% when Coms are minted, 3% when a sale clears — and nothing else. No pricing to negotiate, no tier to defend, no discount to grant. Revenue is linear in throughput and indifferent to whether the buyer is a person, an agent, or an agent buying from an agent.",
    },
    {
      k: "Every claim is verifiable",
      v: "Point your own agent at the live system and it will audit us. Solvency of the currency, network latency, the reputation arena, the prompt library — all public reads, no credentials. We would rather you check than believe.",
    },
    {
      k: "Clean structure",
      v: "ComOS Federation, Inc. is a Delaware C-corp. All IP is the founder's work product with AI tooling — no contractors, no prior-employer claims. Post-money SAFEs, standard terms.",
    },
    {
      k: "Continuity is a training role",
      v: "The system was built to be run by someone who didn't write it — every change gated, every decision on the record. The first hires include a successor who learns it beside the founder and takes the wheel when ready. The bus-factor question gets answered with a person, not a document.",
    },
  ];
  return (
    <Section tint="sunk">
      <Container>
        <SectionHead
          eyebrow="Is the investment safe"
          title="The expensive risk is already retired. What's left is the kind money is for."
          lead="Most seed rounds fund a build and hope for a market. This one funds distribution into a market whose arrival has a date."
        />
        <motion.div
          {...inView}
          variants={stagger}
          className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3"
        >
          {items.map((it) => (
            <motion.div key={it.k} variants={fadeUp}>
              <Card className="h-full">
                <h3 className="text-display-sm mb-2">{it.k}</h3>
                <p className="text-mute dark:text-mute-soft text-sm">{it.v}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>
        <motion.div {...inView} variants={fadeUp} className="mt-14">
          <Card className="max-w-4xl mx-auto">
            <TwoSidedCut />
          </Card>
        </motion.div>
      </Container>
    </Section>
  );
}

/* ── 7. The round ───────────────────────────────────────────────────── */
function Round() {
  return (
    <Section>
      <Container>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <SectionHead
            eyebrow="The round"
            title="Rolling SAFEs now. A priced seed when the catalysts land."
            lead="Two stages, and the wave sets the clock. One of the three catalysts is already true."
          />
          <motion.div {...inView} variants={stagger} className="grid gap-4">
            {[
              {
                k: "Now — rolling SAFEs, ~$1–1.5M",
                v: "Post-money SAFEs. Operator-angels and solo-founder-friendly checks. The cost structure is flat and rent-free, with no payroll yet — there is no forced-raise cliff.",
              },
              {
                k: "Next — priced seed, $4–6M",
                v: "After the catalysts: iOS 27 GA on a billion devices (landed 2026-09-14), a throughput curve, and the first settlement by an agent we did not build. Two of three is a go.",
              },
              {
                k: "What the money buys — people first",
                v: "A marketing lead who sells the idea and runs the marketing desk by prompting it. A successor in training who works beside the founder and drives when ready — possibly two. A merchant-side operator at the first outside installs. Then onboarding at scale and trust hardening; infrastructure spend follows throughput, never precedes it.",
              },
            ].map((it) => (
              <motion.div key={it.k} variants={fadeUp}>
                <Card className="h-full">
                  <h3 className="text-display-sm mb-1.5">{it.k}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {it.v}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Container>
    </Section>
  );
}

/* ── 8. Verify — the trusted-messenger route ────────────────────────── */
function Verify() {
  return (
    <Section tint="sunk">
      <Container width="prose">
        <SectionHead
          eyebrow="Don't take our word for it"
          title="Have your own agent run diligence on the live system."
          lead="Paste one sentence into Claude, ChatGPT, or any agent that can read a URL. It reads our machine-legible disclosure and reports back in the voice you already trust."
        />
        <motion.div {...inView} variants={fadeUp} className="mt-10">
          <PromptCard
            eyebrow="Paste this into your agent"
            prompt={VERIFY_PROMPT}
            after="No account, no MCP setup. Every term in the disclosure is verifiable by calling the tool that enforces it — solvency, latency, the reputation arena, and the prompt library are all public reads."
          />
        </motion.div>
      </Container>
    </Section>
  );
}

/* ── 9. Documents ───────────────────────────────────────────────────── */
function Documents() {
  return (
    <Section>
      <Container>
        <SectionHead
          eyebrow="Go deeper"
          title="The documents."
          lead="Generated from the same fact-checked sources the system runs on. Start with the first one; the rest are the floor under it."
        />
        <motion.div
          {...inView}
          variants={stagger}
          className="mt-10 grid gap-4 md:grid-cols-2"
        >
          <motion.div variants={fadeUp}>
            <a href={PITCH_DECK.href} download className="block h-full">
              <Card hover className="h-full">
                <h3 className="text-display-sm mb-1">{PITCH_DECK.title} ↓</h3>
                <p className="text-mute dark:text-mute-soft text-sm">
                  {PITCH_DECK.blurb}
                </p>
              </Card>
            </a>
          </motion.div>
          <motion.div variants={fadeUp}>
            <a href={PITCH_DECK_PDF.href} download className="block h-full">
              <Card hover className="h-full">
                <h3 className="text-display-sm mb-1">{PITCH_DECK_PDF.title} ↓</h3>
                <p className="text-mute dark:text-mute-soft text-sm">
                  {PITCH_DECK_PDF.blurb}
                </p>
              </Card>
            </a>
          </motion.div>
          {INVESTOR_DOCS.map((doc) => (
            <motion.div key={doc.slug} variants={fadeUp}>
              <Link to={`/investors/${doc.slug}`} className="block h-full">
                <Card hover className="h-full">
                  <h3 className="text-display-sm mb-1">{doc.title}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {doc.blurb}
                  </p>
                </Card>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </Container>
    </Section>
  );
}

/* ── 10. Close ──────────────────────────────────────────────────────── */
function Close() {
  return (
    <Section tint="ink">
      <Container className="text-center">
        <motion.div {...inView} variants={fadeUp} className="max-w-2xl mx-auto">
          <h2 className="text-display-lg text-balance text-paper">
            An entire business on a box you own. Every operation a prompt away.
          </h2>
          <p className="text-lead text-paper/70 mt-6">
            The network is live. The wave is dated. The clock is running.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={FOUNDER_MAILTO}
              className={buttonStyles({ variant: "primary", size: "lg" })}
            >
              Talk to the founder
            </a>
            <a
              href={PITCH_DECK.href}
              download
              className={buttonStyles({
                variant: "outline",
                size: "lg",
                className:
                  "border-paper/25 text-paper hover:text-white hover:border-paper",
              })}
            >
              Download the deck
            </a>
          </div>
          <p className="mt-8 text-sm text-paper/50">
            Ron Reynolds, founder ·{" "}
            <a className="underline" href={`mailto:${FOUNDER_EMAIL}`}>
              {FOUNDER_EMAIL}
            </a>{" "}
            · diligence requests and the full data room on request
          </p>
        </motion.div>
      </Container>
    </Section>
  );
}

function InvestorsIndex() {
  return (
    <>
      <Hero />
      <Pillars />
      <Morning />
      <WhyNow />
      <Future />
      <Safe />
      <Round />
      <Verify />
      <Documents />
      <Close />
    </>
  );
}

export function InvestorsPage() {
  useNoIndex();
  const { slug } = useParams();
  if (!slug) {
    return (
      <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
        <InvestorsIndex />
      </div>
    );
  }

  const doc = getInvestorDoc(slug);
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Section>
        <Container width="prose">
          {doc ? (
            <>
              <Link
                to="/investors"
                className="text-sm text-mute dark:text-mute-soft hover:underline"
              >
                ← All investor documents
              </Link>
              <div className="mt-6">
                <Prose>{doc.body}</Prose>
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <h1 className="text-display-md mb-4">Document not found</h1>
              <p className="text-mute dark:text-mute-soft mb-8">
                That investor document doesn't exist.
              </p>
              <Link
                to="/investors"
                className={buttonStyles({ variant: "outline" })}
              >
                All investor documents
              </Link>
            </div>
          )}
        </Container>
      </Section>
    </div>
  );
}
